// src/inject.ts
var DEFAULT_INSTANCE_KEY = "__default__";
function toTokenName(token) {
  return typeof token === "function" ? token.name : token;
}
function toInstanceKey(name) {
  return name ?? DEFAULT_INSTANCE_KEY;
}
function getRecord(context, token) {
  return context.get("inject:" + toTokenName(token));
}
function ensureRecord(context, token) {
  const key = "inject:" + toTokenName(token);
  let record = context.get(key);
  if (!record) {
    record = {
      multi: false,
      values: /* @__PURE__ */ new Map(),
      injected: /* @__PURE__ */ new Set()
    };
  }
  context.set(key, record);
  return record;
}
function provide(context, token, value, options = {}) {
  const record = ensureRecord(context, token);
  const instanceKey = toInstanceKey(options.name);
  if (options.multi) {
    record.multi = true;
  }
  if (!record.multi && instanceKey !== DEFAULT_INSTANCE_KEY) {
    record.multi = true;
  }
  record.values.set(instanceKey, value);
  return value;
}
function isInjected(context, token, options = {}) {
  const record = getRecord(context, token);
  if (!record) {
    return false;
  }
  if (options.name) {
    return record.injected.has(toInstanceKey(options.name));
  }
  if (record.multi) {
    return record.injected.size > 0;
  }
  return record.injected.has(DEFAULT_INSTANCE_KEY);
}
function isProvided(context, token, options = {}) {
  const record = getRecord(context, token);
  if (!record) {
    return false;
  }
  if (options.name) {
    return record.values.has(toInstanceKey(options.name));
  }
  if (record.multi) {
    return record.values.size > 0;
  }
  return record.values.has(DEFAULT_INSTANCE_KEY);
}
function hasInstance(context, token, options = {}) {
  return isProvided(context, token, options);
}
function handleMissingInjection(token, options) {
  const name = toTokenName(token);
  if (options.name) {
    throw new Error(`Injection provider ${name} with name ${options.name} not found`);
  }
  throw new Error(`Injection provider ${name} not found`);
}
function markInjected(record, key) {
  record.injected.add(key);
}
function markAllInjected(record) {
  for (const key of record.values.keys()) {
    record.injected.add(key);
  }
}
function inject(context, token, options = {}) {
  const record = getRecord(context, token);
  if (!record) {
    if (options.optional) {
      return options.multi ? [] : void 0;
    }
    return handleMissingInjection(token, options);
  }
  if (options.name) {
    const instanceKey = toInstanceKey(options.name);
    if (!record.values.has(instanceKey)) {
      if (options.optional) {
        return void 0;
      }
      return handleMissingInjection(token, options);
    }
    const value2 = record.values.get(instanceKey);
    markInjected(record, instanceKey);
    return value2;
  }
  if (options.multi || record.multi) {
    if (record.values.size === 0) {
      if (options.optional) {
        return [];
      }
      return handleMissingInjection(token, options);
    }
    markAllInjected(record);
    return Array.from(record.values.values());
  }
  const value = record.values.get(DEFAULT_INSTANCE_KEY);
  if (value === void 0) {
    if (options.optional) {
      return void 0;
    }
    return handleMissingInjection(token, options);
  }
  markInjected(record, DEFAULT_INSTANCE_KEY);
  return value;
}
function override(providers, newProvider, options) {
  let { upsert = false, key } = options ?? {};
  if (!key) {
    key = typeof newProvider === "function" ? newProvider.name : newProvider.provide;
  }
  const flatProviders = providers.flat();
  const exists = flatProviders.some((provider) => {
    if (typeof provider === "function") {
      return provider.name === key;
    } else if (typeof provider === "object") {
      return provider.provide === key;
    }
    return false;
  });
  const mappedProviders = flatProviders.map((provider) => {
    if (typeof provider === "function" && provider.name === key) {
      return newProvider;
    } else if (typeof provider === "object" && provider.provide === key) {
      return newProvider;
    }
    return provider;
  });
  if (upsert && !exists) {
    mappedProviders.push(newProvider);
  }
  return mappedProviders;
}
function findProviders(providers, name) {
  const results = [];
  for (const provider of providers) {
    if (Array.isArray(provider)) {
      results.push(...findProviders(provider, name));
    } else if (findProvider(provider, name)) {
      results.push(provider);
    }
  }
  return results;
}
function findProvider(providers, name) {
  if (!Array.isArray(providers)) {
    if (typeof providers === "object" && "provide" in providers) {
      const provider = providers;
      const providerName = typeof provider.provide === "function" ? provider.provide.name : provider.provide;
      if (name instanceof RegExp) {
        if (name.test(providerName)) return providers;
      } else {
        if (providerName === name) return providers;
      }
    }
    return null;
  }
  for (const provider of providers) {
    if (Array.isArray(provider)) {
      const found = findProvider(provider, name);
      if (found) return found;
      continue;
    }
    if (typeof provider === "object" && "provide" in provider) {
      const providerName = typeof provider.provide === "function" ? provider.provide.name : provider.provide;
      if (name instanceof RegExp) {
        if (name.test(providerName)) return provider;
      } else {
        if (providerName === name) return provider;
      }
    }
  }
  return null;
}

// src/merge-config.ts
function processProvider(mergedConfig, baseConfig, provider) {
  if (Array.isArray(provider)) {
    for (const nestedProvider of provider) {
      processProvider(mergedConfig, baseConfig, nestedProvider);
    }
    return;
  }
  const existingProvider = findProvider(baseConfig.providers, provider.provide);
  if (existingProvider) {
    mergedConfig.providers = override(mergedConfig.providers, provider);
  } else {
    mergedConfig.providers.push(provider);
  }
}
function mergeConfig(baseConfig, config) {
  const mergedConfig = {
    ...baseConfig,
    ...config,
    providers: [...baseConfig.providers]
    // Start with a copy of base providers
  };
  for (const provider of config.providers) {
    processProvider(mergedConfig, baseConfig, provider);
  }
  return mergedConfig;
}

// src/provider.ts
function extractProvideOptions(source) {
  if (!source) {
    return void 0;
  }
  const { multi, name } = source;
  if (multi === void 0 && name === void 0) {
    return void 0;
  }
  return { multi, name };
}
function getDeps(provider) {
  if (typeof provider === "function") {
    return provider.deps ?? [];
  }
  return provider.deps ?? [];
}
function sortProviders(providers) {
  const tokenName = (t) => typeof t === "function" ? t.name : t;
  const map = /* @__PURE__ */ new Map();
  for (const p of providers) {
    const token = tokenName(typeof p === "function" ? p : p.provide);
    const list = map.get(token);
    if (list) {
      list.push(p);
    } else {
      map.set(token, [p]);
    }
  }
  const result = [];
  const visited = /* @__PURE__ */ new Set();
  const stack = /* @__PURE__ */ new Set();
  const visit = (token) => {
    const name = tokenName(token);
    if (visited.has(name)) return;
    if (stack.has(name)) {
      throw new Error(`Circular dependency detected for provider ${name}`);
    }
    stack.add(name);
    const providersForToken = map.get(name);
    if (providersForToken) {
      for (const provider of providersForToken) {
        for (const dep of getDeps(provider)) {
          visit(dep);
        }
        result.push(provider);
      }
      visited.add(name);
    }
    stack.delete(name);
  };
  for (const p of providers) {
    const token = typeof p === "function" ? p : p.provide;
    visit(token);
  }
  return result;
}
async function injector(context, providers) {
  providers = providers.flat();
  providers = sortProviders(providers);
  for (const provider of providers) {
    let token;
    let instance;
    let options;
    if (typeof provider === "function") {
      token = provider;
      instance = new provider(context);
      const diOptions = extractProvideOptions(provider.diOptions ?? provider.di);
      options = diOptions;
    } else {
      token = provider.provide;
      options = extractProvideOptions(provider);
      const provideUserClass = provider.useClass;
      const isClass = typeof provideUserClass === "function";
      if (isClass) {
        instance = new provideUserClass(context);
      } else if ("useValue" in provider) {
        instance = provider.useValue;
      } else if ("useFactory" in provider) {
        instance = provider.useFactory?.(context);
        if (instance instanceof Promise) {
          instance = await instance;
        }
      } else if ("useExisting" in provider) {
        instance = inject(context, provider.useExisting);
      }
    }
    provide(context, token, instance, options);
  }
}

// src/context.ts
var Context = class {
  constructor() {
    /** Internal storage for injected values */
    this.values = {};
  }
  /**
   * Sets a value in the context
   * @param key - Unique identifier for the value
   * @param value - Value to store
   */
  set(key, value) {
    this.values[key] = value;
  }
  /**
   * Retrieves a value from the context
   * @param key - Unique identifier for the value
   * @returns The stored value or undefined if not found
   */
  get(key) {
    return this.values[key];
  }
};
export {
  Context,
  findProvider,
  findProviders,
  hasInstance,
  inject,
  injector,
  isInjected,
  isProvided,
  mergeConfig,
  override,
  provide
};
//# sourceMappingURL=index.js.map